// src/pages/DeviceManagement/components/FilterPanel/AdvancedFilters.jsx
// Advanced filtering controls for device management
// Part of Device Management Overhaul - Phase 1

import React, { useState } from 'react';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
  TextField,
  Typography,
  Button,
  Grid
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

const AdvancedFilters = ({
  filters,
  onFilterChange,
  locations = [],
  deviceTypes = [],
  isLoading = false
}) => {
  const [expanded, setExpanded] = useState(false);

  // Status options
  const statusOptions = [
    { value: 'online', label: 'Online', color: 'success.main' },
    { value: 'offline', label: 'Offline', color: 'error.main' }
  ];

  // Quick date presets
  const datePresets = [
    { label: 'Last Hour', value: () => new Date(Date.now() - 60 * 60 * 1000) },
    { label: 'Last 24 Hours', value: () => new Date(Date.now() - 24 * 60 * 60 * 1000) },
    { label: 'Last 7 Days', value: () => new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) },
    { label: 'Last 30 Days', value: () => new Date(Date.now() - 30 * 24 * 60 * 60 * 1000) }
  ];

  const handleLocationChange = (event) => {
    const value = event.target.value;
    onFilterChange({
      ...filters,
      locationId: typeof value === 'string' ? value.split(',').map(Number) : value
    });
  };

  const handleDeviceTypeChange = (event) => {
    const value = event.target.value;
    onFilterChange({
      ...filters,
      deviceTypeId: typeof value === 'string' ? value.split(',').map(Number) : value
    });
  };

  const handleStatusChange = (event) => {
    const value = event.target.value;
    onFilterChange({
      ...filters,
      status: typeof value === 'string' ? value.split(',') : value
    });
  };

  const handleLastSeenAfterChange = (newValue) => {
    onFilterChange({
      ...filters,
      lastSeenAfter: newValue?.toISOString() || null
    });
  };

  const handleLastSeenBeforeChange = (newValue) => {
    onFilterChange({
      ...filters,
      lastSeenBefore: newValue?.toISOString() || null
    });
  };

  const applyDatePreset = (presetValue) => {
    const date = presetValue();
    onFilterChange({
      ...filters,
      lastSeenAfter: date.toISOString(),
      lastSeenBefore: new Date().toISOString()
    });
  };

  const clearAdvancedFilters = () => {
    onFilterChange({
      ...filters,
      locationId: [],
      deviceTypeId: [],
      status: [],
      lastSeenAfter: null,
      lastSeenBefore: null
    });
  };

  // Count active advanced filters
  const activeFilterCount = [
    filters.locationId?.length > 0,
    filters.deviceTypeId?.length > 0,
    filters.status?.length > 0,
    filters.lastSeenAfter,
    filters.lastSeenBefore
  ].filter(Boolean).length;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        sx={{
          mb: 2,
          '&:before': { display: 'none' },
          boxShadow: expanded ? 2 : 1,
          transition: 'all 0.3s ease'
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            '& .MuiAccordionSummary-content': {
              alignItems: 'center',
              justifyContent: 'space-between'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterIcon fontSize="small" />
            <Typography variant="subtitle2" fontWeight={500}>
              Advanced Filters
            </Typography>
          </Box>
          {activeFilterCount > 0 && (
            <Chip
              label={`${activeFilterCount} active`}
              size="small"
              color="primary"
              sx={{ ml: 2 }}
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ width: '100%' }}>
            <Grid container spacing={2}>
              {/* Location Filter */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small" disabled={isLoading}>
                  <InputLabel>Locations</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(filters.locationId) ? filters.locationId : []}
                    onChange={handleLocationChange}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(Array.isArray(selected) ? selected : []).map((value) => {
                          const location = locations.find(l => l.id === value);
                          return (
                            <Chip
                              key={value}
                              label={location?.friendlyName || value}
                              size="small"
                              onDelete={(e) => {
                                e.stopPropagation();
                                const newValues = filters.locationId.filter(id => id !== value);
                                onFilterChange({ ...filters, locationId: newValues });
                              }}
                              deleteIcon={<ClearIcon fontSize="small" />}
                            />
                          );
                        })}
                      </Box>
                    )}
                    MenuProps={{
                      PaperProps: {
                        style: { maxHeight: 48 * 4.5 + 8 }
                      }
                    }}
                  >
                    {locations.map((location) => (
                      <MenuItem key={location.id} value={location.id}>
                        <Checkbox
                          size="small"
                          checked={filters.locationId?.indexOf(location.id) > -1}
                        />
                        <ListItemText
                          primary={location.friendlyName}
                          secondary={location.path}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Device Type Filter */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small" disabled={isLoading}>
                  <InputLabel>Device Types</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(filters.deviceTypeId) ? filters.deviceTypeId : []}
                    onChange={handleDeviceTypeChange}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(Array.isArray(selected) ? selected : []).map((value) => {
                          const type = deviceTypes.find(t => t.id === value);
                          return (
                            <Chip
                              key={value}
                              label={type?.friendlyName || value}
                              size="small"
                              onDelete={(e) => {
                                e.stopPropagation();
                                const newValues = filters.deviceTypeId.filter(id => id !== value);
                                onFilterChange({ ...filters, deviceTypeId: newValues });
                              }}
                              deleteIcon={<ClearIcon fontSize="small" />}
                            />
                          );
                        })}
                      </Box>
                    )}
                    MenuProps={{
                      PaperProps: {
                        style: { maxHeight: 48 * 4.5 + 8 }
                      }
                    }}
                  >
                    {deviceTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        <Checkbox
                          size="small"
                          checked={filters.deviceTypeId?.indexOf(type.id) > -1}
                        />
                        <ListItemText
                          primary={type.friendlyName}
                          secondary={type.type}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Status Filter */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small" disabled={isLoading}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(filters.status) ? filters.status : []}
                    onChange={handleStatusChange}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(Array.isArray(selected) ? selected : []).map((value) => {
                          const status = statusOptions.find(s => s.value === value);
                          return (
                            <Chip
                              key={value}
                              label={status?.label || value}
                              size="small"
                              sx={{
                                backgroundColor: status?.color,
                                color: 'white'
                              }}
                              onDelete={(e) => {
                                e.stopPropagation();
                                const newValues = filters.status.filter(s => s !== value);
                                onFilterChange({ ...filters, status: newValues });
                              }}
                              deleteIcon={<ClearIcon fontSize="small" sx={{ color: 'white' }} />}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Checkbox
                          size="small"
                          checked={filters.status?.indexOf(option.value) > -1}
                        />
                        <ListItemText primary={option.label} />
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: option.color,
                            ml: 1
                          }}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Date Presets */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5 }}>
                    Quick Date Range
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {datePresets.map((preset) => (
                      <Chip
                        key={preset.label}
                        label={preset.label}
                        size="small"
                        variant="outlined"
                        onClick={() => applyDatePreset(preset.value)}
                        disabled={isLoading}
                      />
                    ))}
                  </Box>
                </Box>
              </Grid>

              {/* Date Range Filters */}
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="Last Seen After"
                  value={filters.lastSeenAfter ? new Date(filters.lastSeenAfter) : null}
                  onChange={handleLastSeenAfterChange}
                  disabled={isLoading}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="Last Seen Before"
                  value={filters.lastSeenBefore ? new Date(filters.lastSeenBefore) : null}
                  onChange={handleLastSeenBeforeChange}
                  disabled={isLoading}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true
                    }
                  }}
                />
              </Grid>

              {/* Clear Advanced Filters Button */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button
                    variant="text"
                    color="secondary"
                    startIcon={<ClearIcon />}
                    onClick={clearAdvancedFilters}
                    disabled={activeFilterCount === 0 || isLoading}
                    size="small"
                  >
                    Clear Advanced Filters
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </AccordionDetails>
      </Accordion>
    </LocalizationProvider>
  );
};

export default AdvancedFilters;